import * as styles from './Sidebar.module.css';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowLeft02Icon,
  Menu01Icon,
  MenuCollapseIcon,
} from '@hugeicons/core-free-icons';
import Button from '../ui/Button';
import { NavItem } from '../ui/Nav/types.ts';
import { Link, matchPath, useLocation } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';
import Notifications from '../ui/Notifications';
import type { NotificationData } from '../ui/Notification';
import {
  fetchPortalMe,
  getPortalUserDisplayName,
  getPortalUserInitials,
  getPortalUserRoleLabel,
  PortalUser,
} from '../../api/portal';
import { useMobile } from '../../hooks/useMobile';

const ACTIVE_ITEM_STORAGE_KEY = 'sidebar-active-item';

const FALLBACK_NAV_ITEMS: NavItem[] = [
  {
    id: 'it',
    label: 'IT',
    children: [
      {
        id: '231',
        label: 'IT метрики',
        children: [
          { id: 'it.price-parsing', label: 'Парсинг цен', to: '/metrics/it' },
          {
            id: 'it.corporate-store',
            label: 'Корпоративный магазин',
            to: '/metrics/corporate-store',
          },
        ],
      },
    ],
  },
];

const getItemKey = (item: NavItem) => item.id ?? item.label;

const isNavPathActive = (pathname: string, to?: string) => {
  if (!to) {
    return false;
  }

  if (to.includes(':')) {
    return matchPath({ path: to, end: true }, pathname) !== null;
  }

  return pathname === to || pathname.startsWith(`${to}/`);
};

const itemContainsPath = (item: NavItem, pathname: string): boolean => {
  if (isNavPathActive(pathname, item.to)) {
    return true;
  }

  return (
    item.children?.some(child => itemContainsPath(child, pathname)) ?? false
  );
};

const findTopLevelItemByPath = (
  items: NavItem[],
  pathname: string
): NavItem | null =>
  items.find(item => itemContainsPath(item, pathname)) ?? null;

const getStoredActiveItem = () => {
  try {
    return localStorage.getItem(ACTIVE_ITEM_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to load the active sidebar item', error);
    return null;
  }
};

interface SidebarProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  notifications: NotificationData[];
  onNotificationRead: (notificationId: string) => Promise<void> | void;
}

export const Sidebar = ({
  isOpen: controlledIsOpen,
  onOpenChange,
  notifications,
  onNotificationRead,
}: SidebarProps) => {
  const location = useLocation();
  const isMobile = useMobile();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(() => !isMobile);
  const [navItems, setNavItems] = useState<NavItem[]>(FALLBACK_NAV_ITEMS);
  const [activeItemKey, setActiveItemKey] = useState<string | null>(
    getStoredActiveItem
  );
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);
  const [userRoleLabel, setUserRoleLabel] = useState('');
  const startSwipeX = useRef<number | null>(null);

  const activeItem =
    navItems.find(item => getItemKey(item) === activeItemKey) ?? null;
  const isSidebarOpen = controlledIsOpen ?? internalIsOpen;

  useEffect(() => {
    let isMounted = true;

    const loadSidebarData = async () => {
      const [navigationResult, meResult] = await Promise.all([
        fetch('/api/portal/navigation', {
          method: 'GET',
          credentials: 'include',
        }),
        fetchPortalMe(),
      ]);

      if (isMounted && meResult) {
        setPortalUser(meResult.user);
        setUserRoleLabel(
          getPortalUserRoleLabel(meResult.realm_roles, meResult.user.username)
        );
      }

      try {
        if (!navigationResult.ok) {
          if (isMounted) {
            setNavItems(FALLBACK_NAV_ITEMS);
          }
          return;
        }

        const navigation = (await navigationResult.json()) as NavItem[];

        if (isMounted) {
          setNavItems(
            Array.isArray(navigation) ? navigation : FALLBACK_NAV_ITEMS
          );
        }
      } catch {
        if (isMounted) {
          setNavItems(FALLBACK_NAV_ITEMS);
        }
      }
    };
    void loadSidebarData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const topLevelItem = findTopLevelItemByPath(navItems, location.pathname);

    if (topLevelItem) {
      setActiveItemKey(getItemKey(topLevelItem));
    }
  }, [location.pathname, navItems]);

  useEffect(() => {
    if (!isMobile || !isSidebarOpen) {
      return;
    }

    const { body, documentElement } = document;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const previousHtmlOverflow = documentElement.style.overflow;

    documentElement.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      documentElement.style.overflow = previousHtmlOverflow;
      Object.assign(body.style, previousBodyStyles);
      window.scrollTo(scrollX, scrollY);
    };
  }, [isMobile, isSidebarOpen]);

  const handleLogout = () => {
    window.location.assign('/oauth/logout');
  };

  const handleProfile = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsUserMenuOpen(false);
  };

  const handleItemSelect = (item: NavItem) => {
    const itemKey = getItemKey(item);

    setActiveItemKey(itemKey);

    try {
      localStorage.setItem(ACTIVE_ITEM_STORAGE_KEY, itemKey);
    } catch (error) {
      console.warn('Failed to save the active sidebar item', error);
    }
  };

  const handleBack = () => {
    setActiveItemKey(null);

    try {
      localStorage.removeItem(ACTIVE_ITEM_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to remove the active sidebar item', error);
    }
  };

  const handleSidebarOpenChange = (isOpen: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(isOpen);
    }

    if (!isOpen) {
      setIsUserMenuOpen(false);
    }

    onOpenChange?.(isOpen);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    startSwipeX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (startSwipeX.current === null) return;

    const endX = event.changedTouches[0].clientX;
    const distance = startSwipeX.current - endX;

    // Палец переместился справа налево минимум на 50px
    if (distance > 50) {
      onOpenChange?.(false);
    }

    startSwipeX.current = null;
  };

  const footer = (
    <div
      className={`${styles.footer} ${
        isSidebarOpen ? styles.footer_expanded : ''
      }`}
    >
      <Notifications
        notifications={notifications}
        showLabel={isSidebarOpen}
        onNotificationRead={onNotificationRead}
        className={styles.footer_button}
      />
      <ThemeToggle showLabel={isSidebarOpen} className={styles.footer_button} />
      <Button
        type="button"
        variant="tab"
        size="md"
        fullWidth
        iconOnly={!isSidebarOpen}
        className={styles.footer_button}
        onClick={() => handleSidebarOpenChange(!isSidebarOpen)}
        aria-label={
          isSidebarOpen ? 'Закрыть боковое меню' : 'Открыть боковое меню'
        }
        leftIcon={
          <HugeiconsIcon icon={isSidebarOpen ? MenuCollapseIcon : Menu01Icon} />
        }
      >
        {isSidebarOpen ? 'Свернуть' : null}
      </Button>
    </div>
  );

  const sidebar = (
    <aside
      className={`${styles.sidebar} ${
        isSidebarOpen ? '' : styles.sidebar_closed
      }`}
      aria-hidden={!isSidebarOpen}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.root_container}>
        <div className={styles.header}>
          <div
            className={`${styles.user_card} ${
              isUserMenuOpen ? styles.user_card_open : ''
            }`}
            onClick={() => setIsUserMenuOpen(prev => !prev)}
          >
            <div className={styles.user_card_container}>
              <div className={styles.avatar}>
                {portalUser ? getPortalUserInitials(portalUser) : '…'}
              </div>
              <div className={styles.name_role}>
                <p className={styles.name}>
                  {portalUser
                    ? getPortalUserDisplayName(portalUser)
                    : 'Пользователь'}
                </p>
                <p className={styles.role}>
                  {portalUser ? userRoleLabel : 'Загрузка…'}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              iconOnly
              className={`${styles.open_button} ${
                isUserMenuOpen ? styles.open_button_open : ''
              }`}
              aria-label="Открыть меню пользователя"
              aria-expanded={isUserMenuOpen}
              leftIcon={
                <svg
                  className={styles.open_arrow}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              }
            />

            <div
              className={`
                ${styles.dropdown}
                ${isUserMenuOpen ? styles.dropdown_open : ''}
              `}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.dropdown_actions}>
                <Button
                  variant="ghost"
                  fullWidth
                  className={styles.profile_button}
                  onClick={handleProfile}
                >
                  Профиль
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  className={styles.logout_button}
                  onClick={e => {
                    e.stopPropagation();
                    handleLogout();
                  }}
                >
                  Выход
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.nav_container}>
          {!activeItem &&
            navItems.map(item => {
              return (
                <Button
                  key={getItemKey(item)}
                  variant="tab"
                  className={styles.root_nav_button}
                  active={itemContainsPath(item, location.pathname)}
                  onClick={() => handleItemSelect(item)}
                >
                  {item.label}
                </Button>
              );
            })}
          <div className={styles.links_container}>
            {activeItem && (
              <Button
                variant="ghost"
                fullWidth
                className={styles.back_button}
                onClick={handleBack}
              >
                <HugeiconsIcon icon={ArrowLeft02Icon} size={16} />
                Вернуться назад
              </Button>
            )}
            {activeItem?.children?.map(item => {
              if (!item.children) {
                const isLinkActive = isNavPathActive(
                  location.pathname,
                  item.to
                );

                return (
                  <Link
                    key={getItemKey(item)}
                    to={item.to ?? ''}
                    className={`${styles.tab_link} ${
                      isLinkActive ? styles.tab_link_active : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <div key={getItemKey(item)} className={styles.child_container}>
                  <p className={styles.label}>{item.label}</p>
                  {item.children.map(child => {
                    const isLinkActive = isNavPathActive(
                      location.pathname,
                      child.to
                    );

                    return (
                      <Link
                        key={getItemKey(child)}
                        to={child.to ?? ''}
                        className={`${styles.tab_link} ${
                          isLinkActive ? styles.tab_link_active : ''
                        }`}
                      >
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );

  const content = (
    <>
      {sidebar}
      {footer}
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
};
